import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Check } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/AppShell";

const FREE = ["60-second daily check-in", "One daily insight with 'why'", "7-day trends", "Goals & forgiving habits", "10 Companion messages a month", "Full export & deletion, always"];
const PLUS = ["Everything in Free", "Unlimited Companion conversations", "30-day trends with cycle overlay", "Pattern detection with the numbers", "Weekly AI reflections", "Printable clinician report (1–3 months)"];

export default function Pricing() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [yearly, setYearly] = useState(true);
  const [busy, setBusy] = useState(false);
  const trial = !user || user.trial_eligible;

  const checkout = async () => {
    if (!user) return nav("/register");
    setBusy(true);
    try {
      const { data } = await api.post("/payments/checkout", { lookup_key: yearly ? "camryn_plus_yearly" : "camryn_plus_monthly", origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(errMsg(e)); setBusy(false); }
  };

  return (
    <div className="grain min-h-screen">
      <div className="relative z-10 max-w-4xl mx-auto px-6 py-8">
        <div className="flex items-center justify-between mb-14">
          <Link to={user ? "/today" : "/"}><Logo /></Link>
          {user && <Link to="/today" data-testid="pricing-back-link" className="text-sm text-stone-600">Back to app</Link>}
        </div>
        <div className="eyebrow mb-3 rise">Pricing</div>
        <h1 className="font-display text-4xl sm:text-5xl tracking-tight rise d1">Start free. Go deeper with Plus.</h1>
        <p className="text-stone-600 mt-3 max-w-xl rise d2">The core habit of checking in and getting guidance is free forever. Plus is for women who want the full picture.</p>

        <div className="inline-flex p-1 rounded-full bg-[#EFECE6] mt-8 rise d2">
          {[["Monthly", false], ["Yearly · save 34%", true]].map(([l, v]) => (
            <button key={l} onClick={() => setYearly(v)} data-testid={`pricing-toggle-${v ? "yearly" : "monthly"}`}
              className={`chip px-4 py-2 rounded-full text-sm font-medium ${yearly === v ? "bg-white shadow-sm" : "text-stone-500"}`}>{l}</button>
          ))}
        </div>

        <div className="grid md:grid-cols-2 gap-5 mt-8">
          <div className="card p-7 rise d3" data-testid="pricing-free-card">
            <div className="font-display text-2xl">Free</div>
            <div className="mt-3"><span className="font-display text-4xl">$0</span></div>
            <ul className="mt-6 space-y-3 text-sm">{FREE.map((f) => <li key={f} className="flex gap-2.5"><Check className="h-4 w-4 text-[#6E8B74] mt-0.5" />{f}</li>)}</ul>
            {!user && <Link to="/register" data-testid="pricing-free-cta" className="mt-8 block text-center h-12 leading-[3rem] rounded-full border border-stone-300 font-semibold hover:border-stone-900 chip">Start free</Link>}
          </div>
          <div className="card p-7 rise d4 bg-[#2D2926] border-[#2D2926] text-stone-100" data-testid="pricing-plus-card">
            <div className="font-display text-2xl">CAMRYN Plus</div>
            <div className="mt-3" data-testid="pricing-plus-price">
              <span className="font-display text-4xl">{yearly ? "$79" : "$9.99"}</span>
              <span className="text-stone-400 text-sm"> / {yearly ? "year" : "month"}</span>
              {yearly && <span className="block text-xs text-stone-400 mt-1">About $6.58 a month</span>}
            </div>
            {trial && <div data-testid="pricing-trial-badge" className="mt-4 inline-flex text-xs font-semibold px-3 py-1 rounded-full bg-[#E6B057]/15 text-[#E6B057]">7 days free, then {yearly ? "$79/year" : "$9.99/month"}</div>}
            <ul className="mt-6 space-y-3 text-sm">{PLUS.map((f) => <li key={f} className="flex gap-2.5"><Check className="h-4 w-4 text-[#E6B057] mt-0.5" />{f}</li>)}</ul>
            {user?.plan === "plus" ? (
              <div className="mt-8 text-center text-sm text-stone-300" data-testid="pricing-already-plus">You're on Plus. Thank you.</div>
            ) : (
              <button onClick={checkout} disabled={busy} data-testid="pricing-checkout-btn" className="btn-terra mt-8 w-full h-12 rounded-full font-semibold disabled:opacity-60">
                {busy ? "Opening secure checkout…" : !user ? "Create account to start free trial" : trial ? "Start 7-day free trial" : "Upgrade to Plus"}
              </button>
            )}
            <p className="text-[11px] text-stone-400 mt-3 text-center" data-testid="pricing-fine-print">
              {trial ? "Card required. You won't be charged until day 8, and you can cancel any time before then from Me. " : "Cancel any time. "}Secure payment by Stripe. Test mode.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
